import { Breadcrumbs } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import type { BreadcrumbItem } from '@vizit/utils';
import * as React from 'react';

interface BreadcrumbsBarProps extends React.PropsWithChildren {
  /**
   * The class name to apply to the breadcrumbs bar
   */
  className?: string;
  /**
   * The breadcrumbs to display
   */
  breadcrumbs: BreadcrumbItem[];
  /**
   * The version of the app.
   * @optional
   * @type string
   */
  appVersion?: string;
  /**
   * The name of the organization.
   * @optional
   * @type string
   */
  organizationName?: string;
  /**
   * The color of the organization badge.
   * @optional
   * @type string
   */
  organizationBadgeColor?: string;
  /**
   * The text color of the organization badge.
   * @optional
   * @type string
   */
  organizationBadgeTextColor?: string;
}

const BreadcrumbsBar = React.forwardRef<HTMLElement, BreadcrumbsBarProps>((props, ref) => {
  const { className, breadcrumbs, organizationName, organizationBadgeColor, organizationBadgeTextColor } = props;

  return (
    <nav ref={ref} className={cx('w-full bg-white border-b border-primary py-4 px-8', className)}>
      <div className={cx('h-18 flex items-center justify-between')}>
        <Breadcrumbs items={breadcrumbs} />
        <div className="inline-flex items-center gap-2">
          {organizationName && (
            <div
              className={cx(
                'inline-flex items-center justify-center w-full px-4 py-1 text-sm text-brand-secondary bg-brand-200 border border-brand-600 rounded-full',
              )}
              // Since these are determined at runtime, we need to use style to apply them.
              style={{
                backgroundColor: organizationBadgeColor,
                color: organizationBadgeTextColor,
                borderColor: organizationBadgeColor,
              }}
              role="presentation"
            >
              <span className={cx('text-center text-[inherit] fit-content')}>{organizationName}</span>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
});

export { BreadcrumbsBar };
